import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import { Flex, Grid, Skeleton, Typography } from 'antd';
import styled from 'styled-components';

import { StyledButton } from '@/lib/theme/components/Button';
import PlusIcon from '@/components/Icons/PlusIcon';
import MinusIcon from '@/components/Icons/MinusIcon';
import themeToken from '@lib/theme/tokens/index';
import type { PdfPageEngagement } from '../types';
import { calculatePageVisibility } from '../utils/pdfScrollDepth';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

const { Text } = Typography;
const { useBreakpoint } = Grid;

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const StyledDocument = styled(Document)`
  &.react-pdf__Document {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${themeToken.margin}px;
    padding: ${themeToken.padding}px;

    .react-pdf__Page__canvas {
      margin: auto;
      box-shadow: ${themeToken.boxShadowSecondary};
    }
  }
`;

type Props = {
  fileUrl: string;
  windowWidth: number;
  trackingActive?: boolean;
  onEngagementChange?: (snapshot: PdfEngagementSnapshot, reliable?: boolean) => void;
};

export type PdfEngagementSnapshot = {
  totalPages: number;
  pages: PdfPageEngagement[];
};

export type PdfContainerHandle = {
  getEngagementSnapshot: () => PdfEngagementSnapshot;
};

type LazyPdfPageProps = {
  pageNumber: number;
  pageScale: number;
  pageWidth: number;
  scrollContainer: HTMLDivElement | null;
  registerPage: (pageNumber: number, element: HTMLDivElement | null) => void;
};

const scaleStep = 0.1;
const minScale = 0.5;
const maxScale = 2;
const updateInterval = 10_000;

const LazyPdfPage = ({ pageNumber, pageScale, pageWidth, scrollContainer, registerPage }: LazyPdfPageProps) => {
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(pageNumber === 1);

  const setPageRef = useCallback(
    (element: HTMLDivElement | null) => {
      pageRef.current = element;
      registerPage(pageNumber, element);
    },
    [pageNumber, registerPage],
  );

  useEffect(() => {
    const pageElement = pageRef.current;
    if (!pageElement || !scrollContainer || shouldRender) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { root: scrollContainer, rootMargin: '1000px 0px' },
    );

    observer.observe(pageElement);
    return () => observer.disconnect();
  }, [scrollContainer, shouldRender]);

  return (
    <div
      ref={setPageRef}
      data-page-number={pageNumber}
      style={{
        minHeight: `${pageWidth * pageScale * 1.414}px`,
        width: `${pageWidth * pageScale}px`,
        maxWidth: '100%',
      }}
    >
      {shouldRender && <Page pageNumber={pageNumber} width={pageWidth} scale={pageScale} />}
    </div>
  );
};

const PdfContainer = forwardRef<PdfContainerHandle, Props>(
  ({ fileUrl, windowWidth, trackingActive = true, onEngagementChange }, ref) => {
    const { t } = useTranslation('dataroom');
    const screens = useBreakpoint();
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const animationFrameRef = useRef<number>();
    const pageElementsRef = useRef(new Map<number, HTMLDivElement>());
    const pageEngagementRef = useRef(new Map<number, { durationMs: number; maxDepthPercent: number }>());
    const activePageRef = useRef<number>();
    const activeSinceRef = useRef<number>();
    const totalPagesRef = useRef(0);
    const onEngagementChangeRef = useRef(onEngagementChange);

    const [pdfPages, setPdfPages] = useState(0);
    const [pageScale, setPageScale] = useState(1);
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

    onEngagementChangeRef.current = onEngagementChange;

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
      totalPagesRef.current = numPages;
      setPdfPages(numPages);
      onEngagementChangeRef.current?.({ totalPages: numPages, pages: [] });
    };

    const docOptions = useMemo(
      () => ({
        cMapUrl: '/cmaps/',
        standardFontDataUrl: '/standard_fonts/',
      }),
      [],
    );

    const pdfPageWidth = useMemo(() => {
      const maxPdfWidth = 800;
      const containerPadding = screens.lg ? 120 : 48;

      return Math.max(240, Math.min(windowWidth, maxPdfWidth) - containerPadding);
    }, [screens.lg, windowWidth]);

    const accrueActivePage = useCallback((now: number, force = false) => {
      const activePage = activePageRef.current;
      const activeSince = activeSinceRef.current;
      if (!activePage || activeSince === undefined || (!force && document.visibilityState === 'hidden')) return;

      const engagement = pageEngagementRef.current.get(activePage) ?? { durationMs: 0, maxDepthPercent: 0 };
      engagement.durationMs += Math.max(0, now - activeSince);
      pageEngagementRef.current.set(activePage, engagement);
      activeSinceRef.current = now;
    }, []);

    const captureVisiblePages = useCallback(() => {
      const container = scrollContainerRef.current;
      if (!container) return false;

      const now = performance.now();
      accrueActivePage(now);
      const containerRect = container.getBoundingClientRect();
      let nextActivePage: number | undefined;
      let greatestVisibleHeight = 0;

      pageElementsRef.current.forEach((pageElement, pageNumber) => {
        const pageRect = pageElement.getBoundingClientRect();
        const { visibleHeight, depthPercent } = calculatePageVisibility(containerRect, pageRect);
        if (!visibleHeight) return;

        const engagement = pageEngagementRef.current.get(pageNumber) ?? { durationMs: 0, maxDepthPercent: 0 };
        engagement.maxDepthPercent = Math.max(engagement.maxDepthPercent, depthPercent);
        pageEngagementRef.current.set(pageNumber, engagement);

        if (visibleHeight > greatestVisibleHeight) {
          greatestVisibleHeight = visibleHeight;
          nextActivePage = pageNumber;
        }
      });

      const pageChanged = nextActivePage !== activePageRef.current;
      activePageRef.current = nextActivePage;
      activeSinceRef.current = nextActivePage ? now : undefined;
      return pageChanged;
    }, [accrueActivePage]);

    const getEngagementSnapshot = useCallback((): PdfEngagementSnapshot => {
      captureVisiblePages();
      accrueActivePage(performance.now());

      return {
        totalPages: totalPagesRef.current,
        pages: Array.from(pageEngagementRef.current.entries())
          .map(([pageNumber, engagement]) => ({
            page_number: pageNumber,
            duration: Math.floor(engagement.durationMs / 1000),
            max_depth_percent: engagement.maxDepthPercent,
          }))
          .filter(({ duration, max_depth_percent: maxDepth }) => duration > 0 || maxDepth > 0)
          .sort((first, second) => first.page_number - second.page_number),
      };
    }, [accrueActivePage, captureVisiblePages]);

    useImperativeHandle(ref, () => ({ getEngagementSnapshot }), [getEngagementSnapshot]);

    const emitEngagement = useCallback(
      (reliable = false) => {
        onEngagementChangeRef.current?.(getEngagementSnapshot(), reliable);
      },
      [getEngagementSnapshot],
    );

    const handleScroll = () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = requestAnimationFrame(() => {
        if (captureVisiblePages()) emitEngagement();
      });
    };

    const registerPage = useCallback((pageNumber: number, element: HTMLDivElement | null) => {
      if (element) pageElementsRef.current.set(pageNumber, element);
      else pageElementsRef.current.delete(pageNumber);
    }, []);

    const setScrollContainerRef = useCallback((element: HTMLDivElement | null) => {
      scrollContainerRef.current = element;
      setScrollContainer(element);
    }, []);

    useEffect(() => {
      pageElementsRef.current.clear();
      pageEngagementRef.current.clear();
      activePageRef.current = undefined;
      activeSinceRef.current = undefined;
      totalPagesRef.current = 0;
      setPdfPages(0);
      scrollContainerRef.current?.scrollTo({ top: 0 });
    }, [fileUrl]);

    useEffect(() => {
      if (!pdfPages || !trackingActive) return;
      animationFrameRef.current = requestAnimationFrame(captureVisiblePages);
      const interval = window.setInterval(() => emitEngagement(), updateInterval);

      return () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        window.clearInterval(interval);
      };
    }, [captureVisiblePages, emitEngagement, pageScale, pdfPages, trackingActive, windowWidth]);

    useEffect(() => {
      if (trackingActive) {
        animationFrameRef.current = requestAnimationFrame(captureVisiblePages);
        return;
      }

      emitEngagement(true);
      activePageRef.current = undefined;
      activeSinceRef.current = undefined;
    }, [captureVisiblePages, emitEngagement, trackingActive]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          accrueActivePage(performance.now(), true);
          emitEngagement(true);
        } else activeSinceRef.current = activePageRef.current ? performance.now() : undefined;
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        emitEngagement(true);
      };
    }, [accrueActivePage, emitEngagement]);

    return (
      <div
        ref={setScrollContainerRef}
        onScroll={handleScroll}
        style={{ height: '100%', width: '100%', overflow: 'auto' }}
      >
        <Flex
          align="center"
          gap={8}
          justify="end"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            padding: themeToken.paddingSM,
            background: themeToken['branding-natural-5'],
          }}
        >
          <Text style={{ color: themeToken.colorPrimary, fontWeight: themeToken.fontWeightStrong }}>{t('zoom')}</Text>
          <StyledButton
            onClick={() => setPageScale((current) => +Math.min(maxScale, current + scaleStep).toFixed(1))}
            size="small"
            disabled={pageScale >= maxScale}
          >
            <PlusIcon />
          </StyledButton>
          <StyledButton
            onClick={() => setPageScale((current) => +Math.max(minScale, current - scaleStep).toFixed(1))}
            size="small"
            disabled={pageScale <= minScale}
          >
            <MinusIcon />
          </StyledButton>
        </Flex>

        <StyledDocument
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          options={docOptions}
          onContextMenu={(event: MouseEvent) => event.preventDefault()}
          loading={<Skeleton active />}
        >
          {Array.from({ length: pdfPages }, (_, index) => (
            <LazyPdfPage
              key={index + 1}
              pageNumber={index + 1}
              pageScale={pageScale}
              pageWidth={pdfPageWidth}
              scrollContainer={scrollContainer}
              registerPage={registerPage}
            />
          ))}
        </StyledDocument>
      </div>
    );
  },
);

PdfContainer.displayName = 'PdfContainer';

export default PdfContainer;
